import { Event, EventSourcePolyfill } from 'event-source-polyfill'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatStreamRequest, getChatStreamerEvent } from '../../api/modules/chatApi'
import { useAuth } from '../../context/AuthContext'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { Agent } from '../../types/agentTypes'
import {
  InternetSearchApprovalRequest,
  AnsweredBy,
  AnswerStep,
  ClarificationRequest,
  KnowledgeSource,
  Reference,
  Flag,
  ImageTag,
  ReferredTo,
  StreamBlock,
} from '../../types/chatTypes'
import { HallucinationWarning, ModelWarning } from '../../types/modelTypes'
// Retrieval steps are surfaced only for knowledge-graph tools; every other
// client's tools stream as before, with no step markers.
import { INTERNET_SEARCH_TOOL_NAME, isGraphTool, toolStep } from '../../core/tools'

interface ToolEvent {
  id: string
  name: string
  args?: Record<string, unknown>
  duration_ms?: number
}

// A running tool's step inside its run (internet search: the checked queries that leave).
interface ToolProgressEvent {
  id: string
  name: string
  data: { queries?: string[] }
}

interface ErrorEvent extends Event {
  type: 'error'
  status: number
  statusText: string
  error?: undefined
}

interface MessageEvent {
  message: string
  response_source?: 'internal_knowledge' | 'agent_llm'
  // When set, `message` is the full, final answer text (e.g. a
  // clarification question, or a bail-route answer that was never
  // streamed token-by-token) and must replace the accumulated streaming
  // buffer rather than being appended onto it.
  replace?: boolean
}

interface MessageInfoEvent {
  message_id: string
  knowledge_source: KnowledgeSource
  references?: Reference[]
  response_source?: 'internal_knowledge' | 'agent_llm'
  additional_kwargs?: { web_search_queries?: string[] }
}

interface FlagEvent {
  flags: Flag[]
}

// Stream preamble: which deployment is answering, and whether that differs
// from what was asked for. Arrives before any content.
interface ModelInfoEvent {
  model_used: string | null
  model_warning: ModelWarning | null
}

type HallucinationWarningEvent = HallucinationWarning

interface SourcesEvent {
  references?: Reference[]
}

interface ImagesEvent {
  images?: ImageTag[]
}

// Mid-stream knowledge-source announcement (searching while a tool runs);
// the final message_info remains authoritative.
interface KnowledgeSourceEvent {
  knowledge_source: KnowledgeSource
  searching: boolean
}
interface ResponseTransitionEvent {
  response_source: 'internal_knowledge' | 'agent_llm'
  phase: 'internal_streaming' | 'llm_resumed'
}

interface DualResponse {
  internal_knowledge: string
  agent_llm: string
}

interface ServerErrorEvent {
  error_code?: string
  error_message?: string
  details?: {
    violations?: string[]
    validation_type?: string
    raw_error?: string
  }
}

export function useChatStream(
  chatStreamRequest: Omit<ChatStreamRequest, 'message'>,
  onMessageComplete: (
    message: string,
    tempId: string,
    messageId: string,
    knowledgeSource?: KnowledgeSource,
    references?: Reference[],
  ) => void,
  onStreamingUpdate: (
    partialMessage: string,
    tempId: string,
    messageId?: string,
    error?: Error & { httpStatus?: number },
    knowledgeSource?: KnowledgeSource,
    errorDetails?: { policy_violations?: string[] },
    responseSource?: 'internal_knowledge' | 'agent_llm',
    isSearchingKnowledge?: boolean,
  ) => void,
  onResponseTransition?: (
    tempId: string,
    responseSource: 'internal_knowledge' | 'agent_llm',
    phase: 'internal_streaming' | 'llm_resumed',
  ) => void,
  onFlagsReceived?: (flags: Flag[]) => void,
  // Fired when sources stream in ahead of the answer tokens, so the in-progress
  // answer can resolve inline citation markers live.
  onReferencesReceived?: (references: Reference[], tempId: string) => void,
  // Fired when the turn's image tags stream in ahead of the answer tokens, so
  // the in-progress answer can resolve inline `[IMG#]` markers to their asset
  // image live.
  onImagesReceived?: (images: ImageTag[], tempId: string) => void,
  // Fired when the agent ends its turn with a scoping question. The intro text
  // has already arrived as a `message{replace}` frame; this carries the options.
  onClarification?: (payload: ClarificationRequest, tempId: string) => void,
  // Fired as graph retrieval steps and answer text arrive, so the bubble can
  // show each step where it happened. Never fires for clients without graph tools.
  onStreamBlocks?: (blocks: StreamBlock[], tempId: string) => void,
  // Fired when a risk-labelled model answered an HR-classified question, so the
  // answer can carry a "verify this against the source" note.
  onHallucinationWarning?: (warning: HallucinationWarning, tempId: string) => void,
  // Fired before any content on a routed main-chat turn: which agent writes it.
  onAnsweredBy?: (agent: AnsweredBy, tempId: string) => void,
  // Fired when the answering agent points the user to another assistant.
  onReferredTo?: (referral: ReferredTo, tempId: string) => void,
  // Fired when an internet search waits for the user's approval in a popup;
  // the turn continues through a resume request.
  onInternetSearchApproval?: (payload: InternetSearchApprovalRequest, tempId: string) => void,
  // Fired with the final envelope when the answer sent internet search queries.
  onWebSearchQueries?: (queries: string[], tempId: string) => void,
  // Fired when a step the user waits on starts (a step) or ends (none).
  onStep?: (step: AnswerStep | undefined, tempId: string) => void,
) {
  const { t } = useTranslation()
  const [streamingAnswer, setStreamingAnswer] = useState('')
  const [dualResponse, setDualResponse] = useState<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const [currentResponseSource, setCurrentResponseSource] = useState<'internal_knowledge' | 'agent_llm' | null>(null)
  const [newMessageId, setNewMessageId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { refreshUser } = useAuth()
  const [knowledgeSource, setKnowledgeSource] = useState<KnowledgeSource>('LLM_ONLY')
  const [references, setReferences] = useState<Reference[]>([])

  const streamingAnswerRef = useRef('')
  const streamBlocksRef = useRef<StreamBlock[]>([])
  // Tools with a waiting step that still run, oldest first: the status line shows the newest,
  // so a tool that ends while another runs hands the line back instead of clearing it.
  const runningStepsRef = useRef<{ id: string; step: AnswerStep }[]>([])
  const dualResponseRef = useRef<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const currentResponseSourceRef = useRef<'internal_knowledge' | 'agent_llm' | null>(null)
  const knowledgeSourceRef = useRef<KnowledgeSource>('LLM_ONLY')
  const newMessageIdRef = useRef('')
  const referencesRef = useRef<Reference[]>([])
  const sourceRef = useRef<EventSourcePolyfill | null>(null)
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const STREAM_TIMEOUT_MS = 180000

  // Blocks are published only once a graph retrieval has happened, so clients
  // without graph tools never receive them and keep the plain-text rendering.
  const publishBlocks = useCallback(
    (tempId: string) => {
      if (!onStreamBlocks) return
      if (!streamBlocksRef.current.some(block => block.kind === 'tool')) return
      onStreamBlocks([...streamBlocksRef.current], tempId)
    },
    [onStreamBlocks],
  )

  const appendText = useCallback(
    (text: string, replace: boolean, tempId: string) => {
      if (replace) {
        // A replace frame supersedes everything streamed so far, but the
        // retrieval steps still happened, so keep them and re-anchor the text.
        streamBlocksRef.current = streamBlocksRef.current.filter(block => block.kind === 'tool')
      }
      const last = streamBlocksRef.current[streamBlocksRef.current.length - 1]
      if (last && last.kind === 'text') {
        last.text += text
      } else {
        streamBlocksRef.current.push({ kind: 'text', text })
      }
      publishBlocks(tempId)
    },
    [publishBlocks],
  )

  const setupEventSource = useCallback(
    async (
      requestPayload: ChatStreamRequest,
      tempId: string,
      isSecondary = false,
    ): Promise<EventSourcePolyfill | null> => {
      try {
        const source = await getChatStreamerEvent(requestPayload)
        if (!source) {
          throw new Error('Could not establish connection. Please try again.')
        }
        sourceRef.current = source

        // Stream timeout: fires if no messages arrive within the timeout period
        const startStreamTimeout = () => {
          if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current)
          streamTimeoutRef.current = setTimeout(() => {
            const timeoutError = new Error('STREAM_TIMEOUT')
            setError(timeoutError)
            onStreamingUpdate('', tempId, undefined, timeoutError, undefined, undefined, undefined)
            setIsLoading(false)
            source.close()
            sourceRef.current = null
          }, STREAM_TIMEOUT_MS)
        }

        source.onopen = () => {
          setIsLoading(false)
          startStreamTimeout()
        }

        // Sent every few seconds while attached files are processed.
        source.addEventListener('processing', startStreamTimeout)

        source.onmessage = event => {
          // Clear timeout on first message received
          if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current)
            streamTimeoutRef.current = null
          }
          const payload = JSON.parse(event.data) as MessageEvent

          // Route content based on response_source
          if (payload.response_source) {
            currentResponseSourceRef.current = payload.response_source
            setCurrentResponseSource(payload.response_source)

            dualResponseRef.current[payload.response_source] += payload.message
            setDualResponse({ ...dualResponseRef.current })

            // Pass the specific response content, not combined
            const responseContent = dualResponseRef.current[payload.response_source]
            onStreamingUpdate(
              responseContent,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              payload.response_source,
            )
          } else {
            // Fallback for single responses
            streamingAnswerRef.current = payload.replace
              ? payload.message
              : streamingAnswerRef.current + payload.message
            setStreamingAnswer(streamingAnswerRef.current)
            appendText(payload.message, payload.replace === true, tempId)
            onStreamingUpdate(
              streamingAnswerRef.current,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
            )
          }
        }

        source.addEventListener('message_info', (event: any) => {
          const payload = JSON.parse(event.data) as MessageInfoEvent

          if (payload.references) {
            referencesRef.current = payload.references
            setReferences(payload.references)
          }

          const queries = payload.additional_kwargs?.web_search_queries
          if (queries?.length && onWebSearchQueries && !isSecondary) {
            onWebSearchQueries(queries, tempId)
          }

          newMessageIdRef.current = payload.message_id
          setNewMessageId(payload.message_id)
          knowledgeSourceRef.current = payload.knowledge_source
          setKnowledgeSource(payload.knowledge_source)

          // Pass the appropriate content based on response source
          const contentToUpdate = payload.response_source
            ? dualResponseRef.current[payload.response_source]
            : streamingAnswerRef.current

          onStreamingUpdate(
            contentToUpdate,
            tempId,
            isSecondary ? undefined : payload.message_id,
            undefined,
            isSecondary ? undefined : payload.knowledge_source,
            undefined,
            payload.response_source,
            false, // final envelope — any search is over
          )
        })

        source.addEventListener('response_transition', (event: any) => {
          const payload = JSON.parse(event.data) as ResponseTransitionEvent
          currentResponseSourceRef.current = payload.response_source
          setCurrentResponseSource(payload.response_source)

          // Notify parent component about the transition
          if (onResponseTransition) {
            onResponseTransition(tempId, payload.response_source, payload.phase)
          }
        })

        source.addEventListener('server-error', (event: any) => {
          const payload = JSON.parse(event.data) as ServerErrorEvent
          const errorCode = payload.error_code || 'server_error'
          const errorDetails = payload.details ? { policy_violations: payload.details.violations } : undefined

          let errorToSet: Error
          let messageToPreserve = ''
          const errorDetailsForAnswer: { policy_violations?: string[] } | undefined = errorDetails

          if (errorCode === 'USER_INPUT_CONTENT_VIOLATION') {
            errorToSet = new Error(errorCode)
          } else if (errorCode === 'BOT_RESPONSE_CONTENT_VIOLATION') {
            messageToPreserve = streamingAnswerRef.current
            errorToSet = new Error(errorCode)
          } else {
            // Handle all other server errors
            errorToSet = new Error(errorCode)
          }

          setError(errorToSet)
          // Pass the real message ID if available, so the error can be applied to the correct answer
          onStreamingUpdate(
            messageToPreserve,
            tempId,
            newMessageIdRef.current,
            errorToSet,
            undefined,
            errorDetailsForAnswer,
            undefined,
          )
          setIsLoading(false)

          source.close()
          sourceRef.current = null
        })


        source.addEventListener('model_info', (event: any) => {
          const payload = JSON.parse(event.data) as ModelInfoEvent
          // The first frame of the agent's run: attached files are processed by now.
          if (!isSecondary) onStep?.(undefined, tempId)
          if (!payload.model_warning) {
            return
          }
          // Non-blocking: the answer is valid, the user just deserves to
          // know which model wrote it.
          const notice: string = t('models.fallbackToast', {
            fallbackModel: payload.model_warning.fallback_model,
            originalModel: payload.model_warning.original_model,
          })
          toast.info(notice, { toastId: `model-fallback-${tempId}`, autoClose: 5000 })
        })

        source.addEventListener('answered_by', (event: any) => {
          const payload = JSON.parse(event.data) as AnsweredBy
          if (onAnsweredBy && !isSecondary) {
            onAnsweredBy(payload, tempId)
          }
        })

        source.addEventListener('referred_to', (event: any) => {
          const payload = JSON.parse(event.data) as ReferredTo
          if (onReferredTo && !isSecondary) {
            onReferredTo(payload, tempId)
          }
        })

        source.addEventListener('hallucination_warning', (event: any) => {
          const payload = JSON.parse(event.data) as HallucinationWarningEvent
          if (onHallucinationWarning) {
            onHallucinationWarning(payload, tempId)
          }
        })

        source.addEventListener('flags', (event: any) => {
          const payload = JSON.parse(event.data) as FlagEvent
          if (payload.flags && onFlagsReceived) {
            onFlagsReceived(payload.flags)
          }
        })

        source.addEventListener('knowledge_source', (event: any) => {
          const payload = JSON.parse(event.data) as KnowledgeSourceEvent
          // Deliberately no knowledgeSourceRef write: the ref feeds the
          // onerror completion path and must only hold the authoritative
          // message_info value, never a provisional announcement that
          // _finalize may still downgrade.
          setKnowledgeSource(payload.knowledge_source)
          if (!isSecondary) {
            onStreamingUpdate(
              streamingAnswerRef.current,
              tempId,
              undefined,
              undefined,
              payload.knowledge_source,
              undefined,
              undefined,
              payload.searching,
            )
          }
        })

        source.addEventListener('sources', (event: any) => {
          const payload = JSON.parse(event.data) as SourcesEvent
          if (payload.references) {
            referencesRef.current = payload.references
            setReferences(payload.references)
            if (onReferencesReceived) {
              onReferencesReceived(payload.references, tempId)
            }
          }
        })

        source.addEventListener('images', (event: any) => {
          const payload = JSON.parse(event.data) as ImagesEvent
          if (payload.images && onImagesReceived) {
            onImagesReceived(payload.images, tempId)
          }
        })

        source.addEventListener('tool_start', (event: any) => {
          const payload = JSON.parse(event.data) as ToolEvent
          const step = toolStep(payload.name, payload.args)
          if (step && !isSecondary) {
            runningStepsRef.current.push({ id: payload.id, step })
            onStep?.(step, tempId)
          }
          if (!isGraphTool(payload.name)) return
          streamBlocksRef.current.push({
            kind: 'tool',
            id: payload.id,
            name: payload.name,
            status: 'active',
            args: payload.args,
          })
          publishBlocks(tempId)
        })

        source.addEventListener('tool_progress', (event: any) => {
          const payload = JSON.parse(event.data) as ToolProgressEvent
          if (payload.name !== INTERNET_SEARCH_TOOL_NAME || isSecondary) return
          const step: AnswerStep = { kind: 'internet', items: payload.data.queries ?? [] }
          runningStepsRef.current.push({ id: payload.id, step })
          onStep?.(step, tempId)
        })

        source.addEventListener('tool_end', (event: any) => {
          const payload = JSON.parse(event.data) as ToolEvent
          const running = runningStepsRef.current
          if (running.some(r => r.id === payload.id)) {
            runningStepsRef.current = running.filter(r => r.id !== payload.id)
            onStep?.(runningStepsRef.current[runningStepsRef.current.length - 1]?.step, tempId)
          }
          const block = streamBlocksRef.current.find(
            b => b.kind === 'tool' && b.id === payload.id,
          )
          if (!block || block.kind !== 'tool') return
          block.status = 'done'
          block.durationMs = payload.duration_ms
          publishBlocks(tempId)
        })

        source.addEventListener('clarification_request', (event: any) => {
          const payload = JSON.parse(event.data) as ClarificationRequest
          if (payload.questions?.length && onClarification) {
            onClarification(payload, tempId)
          }
        })

        source.addEventListener('internet_search_approval', (event: any) => {
          const payload = JSON.parse(event.data) as InternetSearchApprovalRequest
          // The popup replaces the streamed text: nothing is left for the close
          // (onerror) to complete, and no timeout runs while the user decides.
          streamingAnswerRef.current = ''
          dualResponseRef.current = { internal_knowledge: '', agent_llm: '' }
          if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current)
            streamTimeoutRef.current = null
          }
          onInternetSearchApproval?.(payload, tempId)
        })

        source.onerror = error => {
          const errorEvent = error as ErrorEvent
          if (errorEvent && errorEvent.status) {
            let errorMessage = 'An unknown error occurred'
            if (errorEvent.status === 409) {
              // The resume endpoint: that popup was already answered.
              errorMessage = 'INTERNET_SEARCH_NOT_WAITING'
            } else if (errorEvent.status === 401) {
              refreshUser && refreshUser()
              errorMessage = `${errorEvent.statusText}. Probeer opnieuw`
            } else {
              errorMessage = `${errorEvent.statusText}. Contacteer support`
            }
            // The status tells a failed request (retryable when 5xx) from an
            // error the run itself streamed.
            const backendError = Object.assign(new Error(errorMessage), { httpStatus: errorEvent.status })
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined)
          } else if (errorEvent?.error) {
            const backendError = new Error(errorEvent?.error)
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined)
          }
          setIsLoading(false)

          // For dual responses, complete each response separately
          if (
            dualResponseRef.current.internal_knowledge.trim() !== '' ||
            dualResponseRef.current.agent_llm.trim() !== ''
          ) {
            // Complete internal knowledge response if it exists
            if (dualResponseRef.current.internal_knowledge.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.internal_knowledge,
                tempId,
                newMessageIdRef.current,
                'INTERNAL_KB',
                referencesRef.current,
              )
            }

            // Complete agent LLM response if it exists
            if (dualResponseRef.current.agent_llm.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.agent_llm,
                tempId,
                newMessageIdRef.current,
                knowledgeSourceRef.current || 'LLM_ONLY',
                referencesRef.current,
              )
            }
          } else if (streamingAnswerRef.current.trim() !== '') {
            // Fallback for single responses
            onMessageComplete(
              streamingAnswerRef.current,
              tempId,
              newMessageIdRef.current,
              knowledgeSourceRef.current,
              referencesRef.current,
            )
          }

          setStreamingAnswer('')
          source.close()
        }

        return source
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined)
        return null
      }
    },
    [
      refreshUser,
      onMessageComplete,
      onStreamingUpdate,
      onResponseTransition,
      onFlagsReceived,
      onReferencesReceived,
      onImagesReceived,
      onClarification,
      appendText,
      publishBlocks,
      onHallucinationWarning,
      onAnsweredBy,
      onReferredTo,
      onInternetSearchApproval,
      onWebSearchQueries,
      onStep,
      t,
    ],
  )

  const sendMessage = useCallback(
    async (
      message: string,
      tempId: string,
      agent?: Agent,
      assetIds: string[] = [],
      addiontalPayload?: Partial<ChatStreamRequest>,
    ) => {
      try {
        setIsLoading(true)
        setError(null)
        setStreamingAnswer('')
        streamingAnswerRef.current = ''
        streamBlocksRef.current = []
        runningStepsRef.current = []
        setReferences([])
        referencesRef.current = []

        // Reset dual response objects
        const initialDualResponse = { internal_knowledge: '', agent_llm: '' }
        setDualResponse(initialDualResponse)
        dualResponseRef.current = initialDualResponse
        setCurrentResponseSource(null)
        currentResponseSourceRef.current = null

        // Close any existing streams
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }

        const requestPayload: ChatStreamRequest = {
          ...chatStreamRequest,
          ...addiontalPayload,
          message,
          fileIds: assetIds,
        }

        // Agent can be absent for existing chats
        if (agent) {
          requestPayload.agentId = agent.id
        }

        // Remove different handling based on selectedTool; always create a single stream per call
        const source = await setupEventSource(requestPayload, tempId)
        if (source) {
          sourceRef.current = source
        } else {
          throw new Error('Could not establish connection. Please try again.')
        }
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined)

        // Close any streams that might have been opened
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }
      }
    },
    [chatStreamRequest, setupEventSource, onStreamingUpdate],
  )

  // Abort an in-flight stream: close the EventSource and drop the loading
  // state. This stops the client consuming the response; the backend run is
  // cancelled separately (DVG-222).
  const stopStream = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current)
      streamTimeoutRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.close()
      sourceRef.current = null
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        sourceRef.current.close()
        sourceRef.current = null
      }
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current)
        streamTimeoutRef.current = null
      }
    }
  }, [])

  return {
    streamingAnswer,
    dualResponse,
    currentResponseSource,
    newMessageId,
    isLoading,
    error,
    knowledgeSource,
    references,
    sendMessage,
    stopStream,
  }
}
